import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useEbay from "../../data/useEbay";
import useShopifyShippingPolicySync from "../../data/useShopifyShippingPolicySync";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import formatDateTime from "../../utils/formatDateTime";
import { DefaultResponse } from "../../models/system";
import CreateShopifyProfileModal from "../../components/shopifyShippingPolicy/CreateShopifyProfileModal";
import {
  IShopifyProfileDraft,
  IShopifyDeliveryProfile,
  IShopifyShippingPolicyMap,
  IShopifyStore,
} from "../../models/ShopifyShippingPolicy";

const emptyOption = "";

export default function ShopifyShippingPolicySync() {
  const { setInfo } = usePageNameContext();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Shopify shipping policy map");
  const { policies } = useEbay();

  const [selectedStoreId, setSelectedStoreId] = useState<string>(emptyOption);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string>(emptyOption);
  const [selectedDeliveryProfileId, setSelectedDeliveryProfileId] = useState<string>(emptyOption);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [createProfileModalOpen, setCreateProfileModalOpen] = useState<boolean>(false);
  const [dryRunResult, setDryRunResult] = useState<{ message: string; ranAt: string } | null>(null);

  const {
    stores,
    mappings,
    deliveryProfiles,
    validation,
    upsertMapping,
    deleteMapping,
    applyMappings,
    previewProfileFromEbay,
    createProfileFromDraft,
  } = useShopifyShippingPolicySync(selectedStoreId || undefined);

  useEffect(() => {
    setInfo({
      name: "Shopify Shipping Sync",
      desc: "Map eBay fulfillment policies to Shopify delivery profiles",
    });
  }, []);

  useEffect(() => {
    if (selectedStoreId !== emptyOption || !stores.data?.data?.length) return;
    setSelectedStoreId(stores.data.data[0].id);
  }, [stores.data?.data, selectedStoreId]);

  const selectedStore = useMemo(
    () => stores.data?.data?.find((store) => store.id === selectedStoreId),
    [stores.data?.data, selectedStoreId]
  );

  const selectedPolicy = useMemo(
    () => policies.data?.data?.fulfilmentPolicies?.find((policy) => policy.id === selectedPolicyId),
    [policies.data?.data?.fulfilmentPolicies, selectedPolicyId]
  );

  const selectedDeliveryProfile = useMemo(
    () => deliveryProfiles.data?.data?.find((profile) => profile.id === selectedDeliveryProfileId),
    [deliveryProfiles.data?.data, selectedDeliveryProfileId]
  );

  const handleSaveMapping = async () => {
    if (!selectedStore || !selectedPolicy || !selectedDeliveryProfile) return;

    await upsertMapping.mutateAsync(
      {
        shopifyStoreId: selectedStore.id,
        fulfillmentPolicyId: selectedPolicy.id,
        fulfillmentPolicyName: selectedPolicy.name,
        deliveryProfileId: selectedDeliveryProfile.id,
        deliveryProfileName: selectedDeliveryProfile.name,
      },
      saveHandlers
    );
  };

  const handleDeleteMapping = async (mapping: IShopifyShippingPolicyMap) => {
    await deleteMapping.mutateAsync(mapping.id, deleteHandlers);
  };

  const handleApplyMappings = async (dryRun: boolean) => {
    const result = await applyMappings.mutateAsync(dryRun, saveHandlers) as DefaultResponse;
    if (dryRun && result?.success)
    {
      setDryRunResult({
        message: result.message,
        ranAt: new Date().toISOString(),
      });
    }
  };

  const handlePreviewProfileFromEbay = async (shopifyStoreId: string, fulfillmentPolicyId: string): Promise<IShopifyProfileDraft | null> => {
    try
    {
      const res = await previewProfileFromEbay.mutateAsync({ shopifyStoreId, fulfillmentPolicyId });
      return res.data;
    }
    catch
    {
      return null;
    }
  };

  const handleCreateProfileFromDraft = async (
    shopifyStoreId: string,
    fulfillmentPolicyId: string,
    fulfillmentPolicyName: string,
    deliveryProfileName: string
  ): Promise<boolean> => {
    try
    {
      await createProfileFromDraft.mutateAsync({
        shopifyStoreId,
        fulfillmentPolicyId,
        fulfillmentPolicyName,
        deliveryProfileName,
      }, saveHandlers);
      return true;
    }
    catch
    {
      return false;
    }
  };

  const hasInitialData = Boolean(
    stores.data?.data || policies.data?.data || mappings.data?.data
  );
  const showInitialLoading =
    !hasInitialData && (stores.isLoading || policies.isLoading || mappings.isLoading);
  if (showInitialLoading) return <LoadingWheel />;

  const guideCards = [
    {
      number: "1",
      numberClasses: "bg-green-50 text-green-700",
      title: "Save mapping",
      description: "Create or update selected policy/profile map for selected store.",
    },
    {
      number: "2",
      numberClasses: "bg-blue-50 text-blue-700",
      title: "Dry run backfill",
      description: "Preview which existing published Shopify products would be updated.",
    },
    {
      number: "3",
      numberClasses: "bg-indigo-50 text-indigo-700",
      title: "Apply backfill",
      description: "Apply mappings to currently published Shopify products.",
    },
    {
      number: "4",
      numberClasses: "bg-rose-50 text-rose-700",
      title: "Delete mapping",
      description: "Remove mapping so future syncs fall back to Shopify general profile.",
    },
  ];

  return (
    <div className="space-y-6">
      <CreateShopifyProfileModal
        open={createProfileModalOpen}
        setOpen={setCreateProfileModalOpen}
        stores={stores.data?.data ?? []}
        ebayPolicies={policies.data?.data?.fulfilmentPolicies ?? []}
        previewLoading={previewProfileFromEbay.isLoading}
        saveLoading={createProfileFromDraft.isLoading}
        onPreview={({ shopifyStoreId, fulfillmentPolicyId }) => handlePreviewProfileFromEbay(shopifyStoreId, fulfillmentPolicyId)}
        onSave={({ shopifyStoreId, fulfillmentPolicyId, fulfillmentPolicyName, deliveryProfileName }) =>
          handleCreateProfileFromDraft(shopifyStoreId, fulfillmentPolicyId, fulfillmentPolicyName, deliveryProfileName)
        }
      />

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-2xl font-semibold text-gray-900">Shopify shipping sync</h3>
              <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1">
                eBay -&gt; Shopify
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Map eBay fulfillment policy to Shopify delivery profile. During publish or update, Wilma assigns the Shopify variant to the mapped delivery profile.
            </p>
          </div>
          <ButtonInput
            label={helpOpen ? "Hide Guide" : "Show Guide"}
            isSubmit={false}
            onClick={() => setHelpOpen((prev) => !prev)}
            classes="!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50"
          />
        </div>

        {helpOpen && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {guideCards.map((card) => (
                <div key={card.number} className="border border-gray-200 rounded-xl p-4 bg-white">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-semibold ${card.numberClasses}`}>
                    {card.number}
                  </span>
                  <p className="mt-3 text-xl font-semibold text-gray-900">{card.title}</p>
                  <p className="mt-1 text-sm text-gray-600 leading-5">{card.description}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-sm font-bold">
                !
              </span>
              <div>
                <p className="text-base font-semibold text-amber-900">Validation table</p>
                <p className="text-sm text-amber-800">
                  Shows whether each mapped Shopify delivery profile still exists on selected store.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Create or update mapping</h3>
          <ButtonInput
            label="Create Shopify Shipping Policy"
            isSubmit={false}
            onClick={() => setCreateProfileModalOpen(true)}
            classes="!bg-gray-700 hover:!bg-gray-800"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Shopify store</p>
            <select
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={emptyOption}>Select store</option>
              {(stores.data?.data ?? []).map((store: IShopifyStore) => (
                <option key={store.id} value={store.id}>
                  {store.domain}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">eBay fulfillment policy</p>
            <select
              value={selectedPolicyId}
              onChange={(e) => setSelectedPolicyId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={emptyOption}>Select policy</option>
              {(policies.data?.data?.fulfilmentPolicies ?? []).map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Shopify delivery profile</p>
            <select
              value={selectedDeliveryProfileId}
              onChange={(e) => setSelectedDeliveryProfileId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={!selectedStoreId}
            >
              <option value={emptyOption}>Select profile</option>
              {(deliveryProfiles.data?.data ?? []).map((profile: IShopifyDeliveryProfile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <ButtonInput
          label="Save Mapping"
          isSubmit={false}
          onClick={handleSaveMapping}
          classes={undefined}
          disabled={!selectedStore || !selectedPolicy || !selectedDeliveryProfile || upsertMapping.isLoading}
          isLoading={upsertMapping.isLoading}
        />
      </div>

      <div className="bg-white rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Validation</h3>
          <div className="flex items-center gap-2">
            <ButtonInput
              label="Dry Run Backfill"
              isSubmit={false}
              onClick={() => handleApplyMappings(true)}
              isLoading={applyMappings.isLoading}
              classes="!bg-gray-700 hover:!bg-gray-800"
            />
            <ButtonInput
              label="Apply Backfill"
              isSubmit={false}
              onClick={() => handleApplyMappings(false)}
              classes={undefined}
              isLoading={applyMappings.isLoading}
            />
          </div>
        </div>

        {dryRunResult && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <p className="text-sm font-semibold text-blue-900">Latest dry-run result</p>
            <p className="text-sm text-blue-800 mt-1">{dryRunResult.message}</p>
            <p className="text-xs text-blue-700 mt-2">
              Ran at {formatDateTime(dryRunResult.ranAt, DateTime.DATETIME_SHORT)}
            </p>
          </div>
        )}

        <div className="overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Store</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Policy</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Profile</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(validation.data?.data ?? []).map((row) => (
                <tr key={`${row.shopifyStoreId}-${row.fulfillmentPolicyId}`}>
                  <td className="px-3 py-2 text-sm text-gray-800">{row.storeDomain || row.shopifyStoreId}</td>
                  <td className="px-3 py-2 text-sm text-gray-800">{row.fulfillmentPolicyName || row.fulfillmentPolicyId}</td>
                  <td className="px-3 py-2 text-sm text-gray-800">{row.deliveryProfileName || row.deliveryProfileId}</td>
                  <td className={`px-3 py-2 text-sm ${row.deliveryProfileExists ? "text-green-700" : "text-red-700"}`}>
                    {row.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 space-y-4">
        <h3 className="text-lg font-medium text-gray-900">
          Existing mappings {selectedStore ? `(${selectedStore.domain})` : ""}
        </h3>

        <div className="overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">eBay policy</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Shopify profile</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Updated</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(mappings.data?.data ?? []).map((mapping) => (
                <tr key={mapping.id}>
                  <td className="px-3 py-2 text-sm text-gray-800">{mapping.fulfillmentPolicyName || mapping.fulfillmentPolicyId}</td>
                  <td className="px-3 py-2 text-sm text-gray-800">{mapping.deliveryProfileName || mapping.deliveryProfileId}</td>
                  <td className="px-3 py-2 text-sm text-gray-800">
                    {formatDateTime(mapping.updated ?? mapping.created, DateTime.DATETIME_SHORT)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <ButtonInput
                      label="Delete"
                      isSubmit={false}
                      onClick={() => handleDeleteMapping(mapping)}
                      classes="!bg-red-600 hover:!bg-red-700"
                      isLoading={deleteMapping.isLoading}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
